import React, { useState, useEffect, useCallback, useMemo, useRef, ChangeEvent } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import './Player.css';

import { processQueue, ProcessQueueOptions } from './utils/fetchTransformQueue';
import { resetQueue } from './utils/fetchTransformQueue';
import { clearScheduledNotes, panicAllNotesOff } from './utils/MIDIPlayer';
import { createLogger } from './utils/logger';
import { toISOStringNoMs } from './utils/time';

import { setBaseURL as setAPIClientBaseURL } from './utils/APIClient';
import { CATEGORY_NAMES, createDefaultCategoryRouting, CategoryRouting, CategoryRoute } from './domain/categoryRouting';
import { PitchSettings, ScaleName } from './domain/pitch';
import { VelocitySettings } from './domain/velocity';

dayjs.extend(utc);

const log = createLogger('player');

// ============================================================
// Constants
// ============================================================

const DEFAULT_START_TIME = '2024-09-18 13:09:24'; // first timestamp in server/data.json

export const SCALE_OPTIONS: ScaleName[] = [
  'chromatic',
  'major',
  'minor',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'locrian',
  'pentatonic',
];

// ============================================================
// Utility Functions
// ============================================================

/**
 * Get the default base URL based on environment.
 */
function getDefaultBaseURL(): string {
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${protocol}//${hostname}${port ? ':' + port : ''}`;
    }
  }
  return 'http://localhost:3001';
}

// ============================================================
// Component
// ============================================================

const Player: React.FC = () => {
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  const playbackStartRef = useRef<dayjs.Dayjs | null>(null);

  // Connection settings
  const [startApiUrl, setStartApiUrl] = useState('');
  const [baseURL, setBaseURL] = useState(getDefaultBaseURL);
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [offsetSeconds, setOffsetSeconds] = useState<number | null>(null);
  const [lastConfigError, setLastConfigError] = useState<string | null>(null);

  // Pitch mapping settings
  const [centroidMin, setCentroidMin] = useState(24);
  const [centroidMax, setCentroidMax] = useState(241);
  const [lowNote, setLowNote] = useState(36);
  const [highNote, setHighNote] = useState(84);
  const [scale, setScale] = useState<ScaleName>('dorian');
  const [invertPitchMapping, setInvertPitchMapping] = useState(true);
  
  // Velocity settings
  const [minVelocity, setMinVelocity] = useState(30);
  const [maxVelocity, setMaxVelocity] = useState(127);
  
  // Note timing
  const [noteLengthMs, setNoteLengthMs] = useState(100);
  const [noteStartJitterMs, setNoteStartJitterMs] = useState(0);

  // Category routing
  const [categoryRouting, setCategoryRouting] = useState<CategoryRouting>(() => createDefaultCategoryRouting());
  const categoryIds = useMemo(() => Object.keys(CATEGORY_NAMES).sort((a, b) => Number(a) - Number(b)), []);
  const pollutionCategoryIds = useMemo(
    () =>
      categoryIds.filter((id) =>
        /(dust|salt|pollution|smoke|ash|sulfate|insect)/i.test(CATEGORY_NAMES[id] || '')
      ),
    [categoryIds]
  );
  const weatherCategoryIds = useMemo(
    () => categoryIds.filter((id) => id !== '-1' && !pollutionCategoryIds.includes(id)),
    [categoryIds, pollutionCategoryIds]
  );

  const setCategoryGroupSelection = useCallback((enabledIds: string[]) => {
    const enabledSet = new Set(enabledIds);
    setCategoryRouting((prev) => {
      const next = { ...prev };
      for (const id of categoryIds) {
        const existing = next[id] || { enabled: true, port: 1, channel: 1 };
        next[id] = { ...existing, enabled: enabledSet.has(id) };
      }
      return next;
    });
  }, [categoryIds]);

  const pollutionOnlySelected = useMemo(() => {
    const allPollutionEnabled = pollutionCategoryIds.every((id) => categoryRouting[id]?.enabled);
    const allWeatherDisabled = weatherCategoryIds.every((id) => !categoryRouting[id]?.enabled);
    return allPollutionEnabled && allWeatherDisabled;
  }, [categoryRouting, pollutionCategoryIds, weatherCategoryIds]);

  const weatherOnlySelected = useMemo(() => {
    const allWeatherEnabled = weatherCategoryIds.every((id) => categoryRouting[id]?.enabled);
    const allPollutionDisabled = pollutionCategoryIds.every((id) => !categoryRouting[id]?.enabled);
    return allWeatherEnabled && allPollutionDisabled;
  }, [categoryRouting, pollutionCategoryIds, weatherCategoryIds]);

  // Computed offset (live calculation)
  const computedOffsetSeconds = useMemo(() => {
    try {
      if (!startTime) return null;
      const now = dayjs.utc();
      const t0 = dayjs.utc(startTime);
      if (!t0.isValid()) return null;
      return now.diff(t0, 'seconds');
    } catch {
      return null;
    }
  }, [startTime]);

  const recomputeOffsetSeconds = useCallback(() => {
    const t0 = dayjs.utc(startTime);
    if (!t0.isValid()) {
      setLastConfigError(`Invalid start time: ${startTime}`);
      return;
    }
    setLastConfigError(null);
    setOffsetSeconds(dayjs.utc().diff(t0, 'seconds'));
  }, [startTime]);

  const adjustOffsetByHours = useCallback((hoursDelta: number) => {
    const secondsDelta = hoursDelta * 3600;
    setOffsetSeconds((prev) => {
      if (Number.isFinite(prev)) {
        return Number(prev) + secondsDelta;
      }
      const fallback = Number.isFinite(computedOffsetSeconds) ? Number(computedOffsetSeconds) : 0;
      return fallback + secondsDelta;
    });
  }, [computedOffsetSeconds]);

  // Sync APIClient base URL with component state
  useEffect(() => {
    setAPIClientBaseURL(baseURL);
  }, [baseURL]);

  // Initialize form with useful defaults
  useEffect(() => {
    const t0 = dayjs.utc(DEFAULT_START_TIME);
    const to = toISOStringNoMs(t0.add(5, 'minute'));
    const from = toISOStringNoMs(t0);
    setStartApiUrl(`${baseURL}/data?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
    setOffsetSeconds(dayjs.utc().diff(t0, 'seconds'));
  }, [baseURL]);

  const applyConfigFromUrl = useCallback(() => {
    setLastConfigError(null);
    try {
      if (!startApiUrl || startApiUrl.trim().length === 0) {
        throw new Error('Start API URL is required');
      }

      let nextBaseURL = baseURL;
      let nextStartTime = startTime;

      if (startApiUrl.startsWith('http://') || startApiUrl.startsWith('https://')) {
        const url = new URL(startApiUrl);
        nextBaseURL = url.origin;

        const fromParam = url.searchParams.get('from');
        if (!fromParam) {
          throw new Error('Start API URL must include a "from=" query parameter');
        }
        nextStartTime = fromParam;
      } else {
        nextStartTime = startApiUrl.trim();
      }

      const t0 = dayjs.utc(nextStartTime);
      if (!t0.isValid()) {
        throw new Error(`Invalid start time: ${nextStartTime}`);
      }

      const now = dayjs.utc();
      const nextOffsetSeconds = now.diff(t0, 'seconds');

      setBaseURL(nextBaseURL);
      setStartTime(nextStartTime);
      setOffsetSeconds(nextOffsetSeconds);
    } catch (e) {
      setLastConfigError((e as Error)?.message || String(e));
    }
  }, [baseURL, startApiUrl, startTime]);

  const startPlayback = async () => {
    try {
      setLastConfigError(null);
      log.debug('starting midi transport');
      log.debug('starting process queue');

      resetQueue();

      const nowStart = dayjs.utc();
      playbackStartRef.current = nowStart;

      const effectiveOffset = Number.isFinite(offsetSeconds) ? offsetSeconds! : computedOffsetSeconds;
      if (!Number.isFinite(effectiveOffset)) {
        throw new Error('Offset seconds is not configured');
      }

      const pitchSettings: PitchSettings = {
        minValue: Number(centroidMin),
        maxValue: Number(centroidMax),
        lowNote: Number(lowNote),
        highNote: Number(highNote),
        scale: scale,
        root: 0,
        invertMapping: invertPitchMapping,
      };

      if (!Number.isFinite(pitchSettings.minValue) || !Number.isFinite(pitchSettings.maxValue) || pitchSettings.maxValue <= pitchSettings.minValue) {
        throw new Error('Centroid min/max must be numbers and max must be > min');
      }
      if (!Number.isFinite(pitchSettings.lowNote) || !Number.isFinite(pitchSettings.highNote) || pitchSettings.highNote < pitchSettings.lowNote) {
        throw new Error('Low/High note must be numbers and high must be >= low');
      }

      const velocitySettings: VelocitySettings = {
        minDelta: 0,
        maxDelta: 241,
        minVelocity: Number(minVelocity),
        maxVelocity: Number(maxVelocity),
      };
      if (!Number.isFinite(velocitySettings.minVelocity) || !Number.isFinite(velocitySettings.maxVelocity) || velocitySettings.maxVelocity < velocitySettings.minVelocity) {
        throw new Error('Min/Max velocity must be numbers and max must be >= min');
      }

      const options: ProcessQueueOptions = {
        currentTime: nowStart,
        offset: effectiveOffset!,
        inboundBufferSize: 200,
        batchSize: 50,
        duration: 5 * 60,
        lookAheadTime: 60,
        pitchSettings,
        velocitySettings,
        categoryRouting,
        noteLengthMs: Number(noteLengthMs),
        noteStartJitterMs: Number(noteStartJitterMs),
      };

      isPlayingRef.current = true;
      
      const pump = async () => {
        while (isPlayingRef.current) {
          await processQueue({
            ...options,
            currentTime: dayjs.utc(),
          });
          await new Promise((r) => setTimeout(r, 250));
        }
      };

      pump().catch((err) => {
        log.error('processQueue pump failed', err);
        isPlayingRef.current = false;
        setIsPlaying(false);
        setLastConfigError(`Playback stopped: ${err?.message || String(err)}`);
      });

      log.debug('process queue started');
      setIsPlaying(true);
      log.info('MIDI data playback started!');

    } catch (error) {
      log.error('Failed to start playback:', error);
      setLastConfigError((error as Error)?.message || String(error));
      alert('Failed to start MIDI playback. Please check MIDI connections.');
    }
  };

  const stopPlayback = () => {
    isPlayingRef.current = false;
    clearScheduledNotes();
    panicAllNotesOff(1).catch(() => {});
    setIsPlaying(false);
  };

  // Helper for number inputs
  const handleNumberChange = (setter: React.Dispatch<React.SetStateAction<number>>) => 
    (e: ChangeEvent<HTMLInputElement>) => setter(Number(e.target.value));

  return (
    <div className="ecPlayerShell">
      <div className="ecPlayerTopBar">
        <div className="ecPlayerInner ecPlayerTopBarInner">
          <div className="ecPlayerTopBarLeft">
            <div>
              <h1 className="ecTitle">Player</h1>
              <p className="ecSubtitle">Minimal controls for API-driven MIDI playback</p>
            </div>
            <div className="ecTag" data-testid="playback-status">{isPlaying ? 'Playing' : 'Stopped'}</div>
          </div>

          <div className="ecPlayerTopBarRight">
            <button 
              className="ecButton ecButtonPrimary" 
              onClick={startPlayback} 
              disabled={isPlaying}
              data-testid="start-button"
            >
              Start
            </button>
            <button 
              className="ecButton ecButtonDanger" 
              onClick={stopPlayback} 
              disabled={!isPlaying}
              data-testid="stop-button"
            >
              Stop
            </button>
          </div>
        </div>
      </div>

      <div className="ecPlayerBody">
        <div className="ecPlayerInner">
          <div className="ecGrid">
            <div className="ecCard ecCardFull">
              <div className="ecCardHeader">
                <h2 className="ecCardTitle">Connection</h2>
              </div>
              <div className="ecFieldStack">
                <div className="ecField">
                  <div className="ecLabel">Start API URL</div>
                  <input 
                    className="ecInput" 
                    value={startApiUrl} 
                    onChange={(e) => setStartApiUrl(e.target.value)} 
                    placeholder={`${baseURL}/data?from=...&to=...`} 
                    disabled={isPlaying}
                    data-testid="start-api-url"
                  />
                  <div className="ecHelp">Paste a `/data` URL that includes `from=`. We'll derive baseURL + offset.</div>
                </div>
                <div className="ecRow">
                  <button 
                    className="ecButton ecButtonGhost" 
                    onClick={applyConfigFromUrl} 
                    disabled={isPlaying}
                    data-testid="apply-url-button"
                  >
                    Apply URL
                  </button>
                </div>
                {lastConfigError && (
                  <div className="ecError" data-testid="config-error">{lastConfigError}</div>
                )}
                <div className="ecKvp">
                  <div className="ecK">baseURL</div>
                  <div className="ecV"><code data-testid="base-url-display">{baseURL}</code></div>
                  <div className="ecK">startTime</div>
                  <div className="ecV"><code data-testid="start-time-display">{startTime}</code></div>
                  <div className="ecK">offsetSeconds</div>
                  <div className="ecV">
                    <code data-testid="offset-display">{Number.isFinite(offsetSeconds) ? offsetSeconds : '(not set)'}</code>
                    <span style={{ marginLeft: 10, color: 'rgba(255,255,255,0.55)' }}>computed now: {computedOffsetSeconds ?? 'n/a'}</span>
                  </div>
                </div>
                <div className="ecRow">
                  <div className="ecField" style={{ maxWidth: 280 }}>
                    <div className="ecLabel">Offset seconds (override)</div>
                    <input 
                      className="ecInput" 
                      type="number" 
                      value={offsetSeconds !== null && Number.isFinite(offsetSeconds) ? offsetSeconds : ''} 
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { setOffsetSeconds(null); return; }
                        const n = Number(v);
                        setOffsetSeconds(Number.isFinite(n) ? n : null);
                      }} 
                      disabled={isPlaying} 
                      placeholder="auto"
                      data-testid="offset-override"
                    />
                    <div className="ecHelp">Clear to fall back to "computed now".</div>
                  </div>
                  <button
                    className="ecButton ecButtonGhost"
                    onClick={() => adjustOffsetByHours(-1)}
                    disabled={isPlaying}
                    data-testid="offset-minus-1h"
                  >
                    -1h
                  </button>
                  <button
                    className="ecButton ecButtonGhost"
                    onClick={() => adjustOffsetByHours(1)}
                    disabled={isPlaying}
                    data-testid="offset-plus-1h"
                  >
                    +1h
                  </button>
                  <button className="ecButton ecButtonGhost" onClick={recomputeOffsetSeconds} disabled={isPlaying}>Recompute</button>
                </div>
              </div>
            </div>

            <div className="ecCard">
              <div className="ecCardHeader">
                <h2 className="ecCardTitle">Pitch Mapping</h2>
              </div>
              <div className="ecFieldStack">
                <div className="ecFieldRow">
                  <div className="ecField">
                    <div className="ecLabel">Max height min</div>
                    <input className="ecInput" type="number" value={centroidMin} onChange={handleNumberChange(setCentroidMin)} disabled={isPlaying} data-testid="centroid-min" />
                  </div>
                  <div className="ecField">
                    <div className="ecLabel">Max height max</div>
                    <input className="ecInput" type="number" value={centroidMax} onChange={handleNumberChange(setCentroidMax)} disabled={isPlaying} data-testid="centroid-max" />
                  </div>
                </div>
                <div className="ecFieldRow">
                  <div className="ecField">
                    <div className="ecLabel">Low note</div>
                    <input className="ecInput" type="number" value={lowNote} onChange={handleNumberChange(setLowNote)} disabled={isPlaying} data-testid="low-note" />
                  </div>
                  <div className="ecField">
                    <div className="ecLabel">High note</div>
                    <input className="ecInput" type="number" value={highNote} onChange={handleNumberChange(setHighNote)} disabled={isPlaying} data-testid="high-note" />
                  </div>
                </div>
                <div className="ecField">
                  <div className="ecLabel">Scale</div>
                  <select className="ecSelect" value={scale} onChange={(e) => setScale(e.target.value as ScaleName)} disabled={isPlaying} data-testid="scale-select">
                    {SCALE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="ecField">
                  <label className="ecRow" style={{ gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={invertPitchMapping}
                      disabled={isPlaying}
                      data-testid="invert-pitch-mapping"
                      onChange={(e) => setInvertPitchMapping(e.target.checked)}
                    />
                    <span className="ecLabel" style={{ margin: 0 }}>Satellite top-down pitch mapping</span>
                  </label>
                </div>
              </div>
              <div className="ecHelp">Enabled by default to match the satellite looking down (higher altitude maps toward lower pitch).</div>
            </div>

            <div className="ecCard">
              <div className="ecCardHeader">
                <h2 className="ecCardTitle">Velocity Mapping</h2>
              </div>
              <div className="ecFieldStack">
                <div className="ecField">
                  <div className="ecLabel">Min velocity (0–127)</div>
                  <input className="ecInput" type="number" value={minVelocity} onChange={handleNumberChange(setMinVelocity)} disabled={isPlaying} data-testid="min-velocity" />
                </div>
                <div className="ecField">
                  <div className="ecLabel">Max velocity (0–127)</div>
                  <input className="ecInput" type="number" value={maxVelocity} onChange={handleNumberChange(setMaxVelocity)} disabled={isPlaying} data-testid="max-velocity" />
                </div>
              </div>
              <div className="ecHelp">Mapped from delta height in each datapoint.</div>
            </div>

            <div className="ecCard">
              <div className="ecCardHeader">
                <h2 className="ecCardTitle">Note Length</h2>
              </div>
              <div className="ecFieldStack">
                <div className="ecField">
                  <div className="ecLabel">Staccato length (ms)</div>
                  <input className="ecInput" type="number" value={noteLengthMs} onChange={handleNumberChange(setNoteLengthMs)} disabled={isPlaying} data-testid="note-length" />
                  <div className="ecHelp">Note-off time for each note.</div>
                </div>
                <div className="ecField">
                  <div className="ecLabel">Start jitter (ms)</div>
                  <input className="ecInput" type="number" value={noteStartJitterMs} onChange={handleNumberChange(setNoteStartJitterMs)} disabled={isPlaying} data-testid="note-jitter" />
                  <div className="ecHelp">Random delay in [0..jitter]. 0 = precise.</div>
                </div>
              </div>
            </div>

            <div className="ecCard ecCardFull">
              <div className="ecCardHeader">
                <h2 className="ecCardTitle">Category Routing</h2>
              </div>
              <div className="ecHelp" style={{ marginBottom: 10 }}>
                Toggle categories on/off and route each category to a target MIDI port+channel.
              </div>
              <div className="ecRow" style={{ marginBottom: 10 }}>
                <button 
                  className="ecButton ecButtonGhost" 
                  disabled={isPlaying} 
                  data-testid="select-all-categories"
                  onClick={() => {
                    setCategoryRouting((prev) => {
                      const next = { ...prev };
                      for (const id of categoryIds) {
                        const existing = next[id] || { enabled: true, port: 1, channel: 1 };
                        next[id] = { ...existing, enabled: true };
                      }
                      return next;
                    });
                  }}
                >
                  Select all
                </button>
                <button 
                  className="ecButton ecButtonGhost" 
                  disabled={isPlaying}
                  data-testid="select-none-categories"
                  onClick={() => {
                    setCategoryRouting((prev) => {
                      const next = { ...prev };
                      for (const id of categoryIds) {
                        const existing = next[id] || { enabled: true, port: 1, channel: 1 };
                        next[id] = { ...existing, enabled: false };
                      }
                      return next;
                    });
                  }}
                >
                  Select none
                </button>
                <button
                  className="ecButton ecButtonGhost"
                  disabled={isPlaying}
                  data-testid="select-pollution-categories"
                  onClick={() => setCategoryGroupSelection(pollutionCategoryIds)}
                >
                  Select pollution
                </button>
                <button
                  className="ecButton ecButtonGhost"
                  disabled={isPlaying}
                  data-testid="select-weather-categories"
                  onClick={() => setCategoryGroupSelection(weatherCategoryIds)}
                >
                  Select weather
                </button>
                <label className="ecRow" style={{ gap: 6, opacity: 0.85 }}>
                  <input type="checkbox" checked={pollutionOnlySelected} readOnly data-testid="pollution-only-selected" />
                  <span style={{ color: 'rgba(255,255,255,0.70)' }}>Pollution only</span>
                </label>
                <label className="ecRow" style={{ gap: 6, opacity: 0.85 }}>
                  <input type="checkbox" checked={weatherOnlySelected} readOnly data-testid="weather-only-selected" />
                  <span style={{ color: 'rgba(255,255,255,0.70)' }}>Weather only</span>
                </label>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {categoryIds.map((id) => {
                  const route: CategoryRoute = categoryRouting[id] || { enabled: true, port: 1, channel: 1 };
                  return (
                    <div key={id} className="ecRow" style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: 10 }}>
                      <label className="ecRow" style={{ gap: 8, minWidth: 90 }}>
                        <input 
                          type="checkbox" 
                          checked={!!route.enabled} 
                          disabled={isPlaying} 
                          data-testid={`category-${id}-enabled`}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            setCategoryRouting((prev) => ({ ...prev, [id]: { ...prev[id], enabled } }));
                          }} 
                        />
                        <span style={{ color: 'rgba(255,255,255,0.70)', width: 34 }}>{id}</span>
                      </label>
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <div style={{ fontSize: 16 }}>{CATEGORY_NAMES[id]}</div>
                      </div>
                      <div className="ecField" style={{ maxWidth: 150, minWidth: 150 }}>
                        <div className="ecLabel">Port</div>
                        <input 
                          className="ecInput" 
                          type="number" 
                          value={route.port} 
                          disabled={isPlaying}
                          data-testid={`category-${id}-port`}
                          onChange={(e) => {
                            const port = Number(e.target.value);
                            setCategoryRouting((prev) => ({ ...prev, [id]: { ...prev[id], port } }));
                          }} 
                        />
                      </div>
                      <div className="ecField" style={{ maxWidth: 150, minWidth: 150 }}>
                        <div className="ecLabel">Channel</div>
                        <input 
                          className="ecInput" 
                          type="number" 
                          value={route.channel} 
                          disabled={isPlaying}
                          data-testid={`category-${id}-channel`}
                          onChange={(e) => {
                            const channel = Number(e.target.value);
                            setCategoryRouting((prev) => ({ ...prev, [id]: { ...prev[id], channel } }));
                          }} 
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Player;

